import React, { useState } from "react";
import { TextField, Divider, Box } from "@mui/material";
import dayjs from "dayjs";
import { Modal, Text } from "$components";
import { approveEdb, refuseEdb } from "$services/EDB.service";
import useAuth from "$hooks/useAuth";
import { useProfileEDBContext } from "../context/ProfileEDBContext";
import { DISPOSITION_LABELS } from "$utils/typeConstants/EDB";

interface Props {
  open: boolean;
  onClose: () => void;
}

const ROW_LABEL_WIDTH = 190;

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <Text style={{ fontWeight: 600, color: "#6b7a99", fontSize: 13 }}>
        {label}
      </Text>
      <Text style={{ fontSize: 13 }}>{value || "—"}</Text>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text
      style={{
        fontWeight: 700,
        fontSize: 12,
        color: "#457DD9",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 6,
      }}
    >
      {children}
    </Text>
  );
}

export default function ApprovalModal({ open, onClose }: Props) {
  const { token } = useAuth();
  const { edb, fetchEdb, idEdb } = useProfileEDBContext();
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<"main" | "refuse">("main");

  const reset = () => {
    setComment("");
    setStep("main");
    setIsSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleApprove = async () => {
    setIsSubmitting(true);
    try {
      await approveEdb(token, idEdb, comment || undefined);
      await fetchEdb();
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefuse = async () => {
    setIsSubmitting(true);
    try {
      await refuseEdb(token, idEdb, comment || undefined);
      await fetchEdb();
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!edb) return null;

  const dockyard = edb.dockyard as any;
  const project = edb.project as any;
  const company = edb.company as any;
  const approver = dockyard?.approver as any;
  const intervenantCompany = edb.intervenantCompany as any;

  const isBatailLog = approver
    ? approver.job === "coordinators" ||
      !approver.companyData ||
      (approver.companyData?.companyTypes || []).includes("logistician")
    : false;
  const approverCompanyName = approver
    ? isBatailLog
      ? "Batail-Log"
      : approver.companyData?.name || "—"
    : "—";

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: `${ROW_LABEL_WIDTH}px 1fr`,
    gap: "6px 12px",
    alignItems: "baseline",
  };

  if (step === "refuse") {
    return (
      <Modal
        open={open}
        title={`Refuser l'EDB n°${edb.edbId}`}
        textValidate={isSubmitting ? "Refus en cours..." : "Confirmer le refus"}
        textCancel="Annuler"
        onValidate={handleRefuse}
        onCancel={() => setStep("main")}
        disableExternalClosing
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 1 }}>
          <Text>Commentaire (optionnel)</Text>
          <TextField
            multiline
            rows={3}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Saisissez une raison..."
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title={`Validation de l'EDB n°${edb.edbId}`}
      textValidate={isSubmitting ? "Validation..." : "Valider l'EDB"}
      textCancel="Refuser l'EDB"
      onValidate={handleApprove}
      onCancel={() => setStep("refuse")}
      onCloseButton={handleClose}
      disableExternalClosing
      maxWidth="sm"
      fullWidth
    >
      <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Approbateur */}
        <Box>
          <SectionTitle>Approbateur</SectionTitle>
          <Box sx={gridStyle}>
            <InfoRow
              label="Nom"
              value={
                approver
                  ? `${approver.firstname} ${approver.lastname?.toUpperCase()}`
                  : "—"
              }
            />
            <InfoRow label="Entreprise" value={approverCompanyName} />
            <InfoRow
              label="Date de création"
              value={
                edb.createdAt ? dayjs(edb.createdAt).format("DD/MM/YYYY") : "—"
              }
            />
          </Box>
        </Box>

        <Divider />

        {/* EDB */}
        <Box>
          <SectionTitle>EDB</SectionTitle>
          <Box sx={gridStyle}>
            <InfoRow label="Entreprise" value={company?.name} />
            <InfoRow
              label="Entreprise intervenante"
              value={intervenantCompany?.name}
            />
            <InfoRow
              label="Chantier"
              value={dockyard ? `${dockyard.name} [${dockyard.code}]` : "—"}
            />
            <InfoRow
              label="Projet"
              value={project ? `${project.name} [${project.code}]` : "—"}
            />
          </Box>
        </Box>

        <Divider />

        {/* Livraison */}
        <Box>
          <SectionTitle>Livraison</SectionTitle>
          <Box sx={gridStyle}>
            <InfoRow
              label={
                edb.dispositionDateIsExact
                  ? "Date souhaitée"
                  : "Date au plus tard"
              }
              value={
                edb.dispositionDate
                  ? dayjs(edb.dispositionDate).format("DD/MM/YYYY")
                  : "—"
              }
            />
            <InfoRow
              label="Type de livraison"
              value={DISPOSITION_LABELS[(edb as any).dispositionType] || "—"}
            />
            <InfoRow
              label="Adresse de livraison"
              value={(() => {
                const parts = [
                  edb.dispositionAdress,
                  edb.dispositionAdress2,
                  edb.dispositionPostalCode && edb.dispositionCity
                    ? `${edb.dispositionPostalCode} ${edb.dispositionCity}`
                    : edb.dispositionPostalCode || edb.dispositionCity,
                  edb.dispositionCountry,
                ].filter(Boolean);
                return parts.length ? parts.join(", ") : "N/A";
              })()}
            />
          </Box>
        </Box>

        <Divider />

        {/* Commentaire */}
        <Box>
          <Text style={{ fontWeight: 600, marginBottom: 4 }}>
            Commentaire (optionnel)
          </Text>
          <TextField
            multiline
            rows={2}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Justifiez votre choix si nécessaire..."
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>
    </Modal>
  );
}
